<template>
  <div class="unlock-encryption-page">
    <button 
      title="Logout"
      class="btn btn-outline-shareguard text-xs logout-button" 
      @click="handleLogout" 
    >
      <i class="fa fa-power-off me-1"></i>
      Logout
    </button>
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-6 col-lg-5">
          <div v-if="initialLoading" class="text-center">
            <div class="spinner-border text-shareguard" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <template v-else>
            <h2 class="card-title text-center mb-4">{{ pageTitle }}</h2>
            <template v-if="!isResetting">
              <form ref="form" @submit.prevent="handleSubmit">
                <div class="mb-3">
                  <material-input
                    id="password"
                    v-model:value="password"
                    type="password"
                    label="Enter Master Unlock Key"
                    :error="!!passwordError"
                    :disabled="loading"
                    :is-required="true"
                    :autocomplete="isInitialSetup ? 'new-password' : 'current-password'"
                  />
                  <div v-if="password && passwordError" class="form-text text-danger">{{ passwordError }}</div>
                </div>
                <div v-if="isInitialSetup" class="mb-3">
                  <material-input
                    id="confirmPassword"
                    v-model:value="confirmPassword"
                    type="password"
                    label="Confirm Master Unlock Key"
                    :error="!!confirmPasswordError"
                    :disabled="loading"
                    :is-required="true"
                    autocomplete="new-password"
                  />
                  <div v-if="confirmPassword && confirmPasswordError" class="form-text text-danger">{{ confirmPasswordError }}</div>
                </div>
                <div class="d-flex justify-content-between align-items-center mt-4">
                  <button
                    v-if="!isInitialSetup"
                    type="button"
                    class="btn btn-link text-danger p-0"
                    :disabled="loading"
                    @click="isResetting = true"
                  >
                    Forgotten Master Unlock Key?
                  </button>
                  <button
                    type="submit"
                    class="btn btn-shareguard"
                    :disabled="!isFormValid || loading"
                  >
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    {{ submitButtonText }}
                  </button>
                </div>
              </form>
            </template>
            <template v-else>
              <div class="alert alert-warning mb-4">
                <h6 class="alert-heading">Warning: This action cannot be undone</h6>
                <p>Resetting your encryption password will:</p>
                <ul>
                  <li>Delete all your existing encryption keys</li>
                  <li>Make all your encrypted files inaccessible</li>
                  <li>Require you to set up encryption again</li>
                </ul>
                <p>Are you sure you want to proceed?</p>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <button
                  type="button"
                  class="btn btn-secondary"
                  :disabled="loading"
                  @click="isResetting = false"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-danger"
                  :disabled="loading"
                  @click="handleResetState"
                >
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                  Reset Password
                </button>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import MaterialInput from "@/components/ui/MaterialInput.vue";
import { msalInstance } from '../plugins/msal';
import { getKeys } from '../services/encryption.service';

export default {
  name: 'UnlockEncryption',
  components: {
    MaterialInput
  },
  data() {
    return {
      password: '',
      confirmPassword: '',
      loading: false,
      initialLoading: true,
      passwordError: '',
      confirmPasswordError: '',
      hasKeys: false,
      isResetting: false
    };
  },
  computed: {
    ...mapState('encryption', ['isInitialized', 'error']),
    isFormValid() {
      if (this.isInitialSetup) {
        return this.password && this.confirmPassword && !this.passwordError && !this.confirmPasswordError;
      }
      return this.password && !this.passwordError;
    },
    isInitialSetup() {
      return !this.hasKeys;
    },
    pageTitle() {
      if (this.isResetting) {
        return 'Reset Encryption';
      }
      return this.isInitialSetup ? 'Set Up Encryption' : 'Unlock Encryption';
    },
    submitButtonText() {
      if (this.isResetting) {
        return 'Reset';
      }
      return this.isInitialSetup ? 'Set Up' : 'Unlock';
    }
  },
  watch: {
    password() {
      this.validatePassword();
      if (this.isInitialSetup) {
        this.validateConfirmPassword();
      }
    },
    confirmPassword() {
      if (this.isInitialSetup) {
        this.validateConfirmPassword();
      }
    }
  },
  async created() {
    try {
      // Redirect if already initialized
      if (this.isInitialized) {
        const redirectPath = sessionStorage.getItem('redirectAfterUnlock') || '/new';
        this.$router.push(redirectPath);
        return;
      }

      // move service call to store
      const response = await getKeys();
      const keys = response.keys;
      this.hasKeys = keys && keys.length > 0 && !sessionStorage.getItem('needsNewEncryption');
    } catch (error) {
      console.error('Error checking for keys:', error);
      this.hasKeys = false;
    } finally {
      this.initialLoading = false;
    }
  },
  methods: {
    ...mapActions('encryption', ['unlockEncryption', 'initializeEncryption', 'clearEncryption']),
    ...mapActions('snackbar', ['showSnackbar']),
    ...mapActions('user', ['logoutAction', 'populateUserDetails']),
    ...mapActions("contacts", ["fetchContactList"]),
    validatePassword() {
      if (!this.password) {
        this.passwordError = 'Password is required';
      } else if (this.password.length < 8) {
        this.passwordError = 'Password must be at least 8 characters';
      } else if (!/[A-Z]/.test(this.password)) {
        this.passwordError = 'Password must contain at least one uppercase letter';
      } else if (!/[a-z]/.test(this.password)) {
        this.passwordError = 'Password must contain at least one lowercase letter';
      } else if (!/[0-9]/.test(this.password)) {
        this.passwordError = 'Password must contain at least one number';
      } else if (!/[^A-Za-z0-9]/.test(this.password)) {
        this.passwordError = 'Password must contain at least one special character';
      } else {
        this.passwordError = '';
      }
    },
    validateConfirmPassword() {
      if (!this.confirmPassword) {
        this.confirmPasswordError = 'Please confirm your password';
      } else if (this.confirmPassword !== this.password) {
        this.confirmPasswordError = 'Passwords do not match';
      } else {
        this.confirmPasswordError = '';
      }
    },
    async handleSubmit() {
      if (!this.isFormValid) return;
      this.loading = true;
      try {
        if (this.isInitialSetup) {
          // Clear the needsNewEncryption flag when setting up new encryption
          await this.initializeEncryption({ password: this.password });
          this.showSnackbar({ title: 'Encryption set up successfully', color: 'success' });
        } else {
          await this.unlockEncryption({ password: this.password });
          this.showSnackbar({ title: 'Encryption unlocked successfully', color: 'success' });
        }
        this.password = '';
        this.confirmPassword = '';

        msalInstance.handleRedirectPromise();
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          this.populateUserDetails();
          this.fetchContactList();
        }

        // Use default path of '/new' if no redirect path is set
        const redirectPath = sessionStorage.getItem('redirectAfterUnlock') || '/new';
        this.$router.push(redirectPath);
      } catch (error) {
        console.error('Encryption error:', error);
        this.showSnackbar({ title: "Encryption Unlock failed. Please try again.", color: 'danger' });
      } finally {
        this.loading = false;
      }
    },
    showForgottenPasswordWarning() {
      this.isResetting = true;
    },
    async handleResetState() {
      this.loading = true;
      try {
        await this.clearEncryption();
        sessionStorage.setItem('needsNewEncryption', 'true');
        this.showSnackbar({ 
          title: 'Encryption state reset. Please set up new encryption with a new password.', 
          color: 'success' 
        });
        this.password = '';
        this.confirmPassword = '';
        this.hasKeys = false;
        this.isResetting = false;
        window.location.reload();
      } catch (error) {
        console.error('Error resetting encryption state:', error);
        this.showSnackbar({ 
          title: 'Error resetting encryption state. Please try again.', 
          color: 'danger' 
        });
      } finally {
        this.loading = false;
      }
    },
    async handleLogout() {
      await this.logoutAction();
      this.$router.push("/login");
    }
  }
};
</script>

<style scoped>
.unlock-encryption-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  background-image: url("~@/assets/img/light_bg.svg");
  background: rgba(255, 255, 255, 0);
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  overflow-x: hidden;
  position: relative;
}

.logout-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  z-index: 1000;
  border-radius: 20px;
  font-size: 0.9rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
}

.logout-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.card-title {
  color: #2c3e50;
  font-weight: 600;
}

.alert {
  margin-bottom: 0;
}

.alert ul {
  margin-bottom: 0;
  padding-left: 1.2rem;
}

.btn-link {
  text-decoration: none;
  padding: 0;
}

.btn-link:hover {
  text-decoration: underline;
}
</style> 